// FaceRegisterModal — used from the Upload page to let a guest attach their
// face to their contributor identity. The selfie is processed entirely in the
// browser via face-api.js; only the 128-dim descriptor is uploaded, never the
// image. Once registered, other guests can find this person's photos across
// the whole album via the guest-face pills on the gallery.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Camera, X as XIcon, ShieldCheck, RefreshCw, ScanFace, Check } from "lucide-react";
import { loadFaceModels, extractSingleFace, cropFaceAvatar, waitForVideoReady } from "../lib/faceApi";
import { Faces } from "../lib/api";

export default function FaceRegisterModal({ open, onClose, slug, guestId, name, onRegistered }) {
  const [stage, setStage] = useState("intro"); // intro | starting | camera | working | done | error
  const [err, setErr] = useState("");
  const [captureErr, setCaptureErr] = useState(""); // shown inline in the camera stage
  // Gallery visitors may not have a stored name yet — let them type one here.
  const [typedName, setTypedName] = useState("");
  const effectiveName = (name || "").trim() || typedName.trim();
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStage("intro");
      setErr("");
      loadFaceModels().catch(() => {});
    }
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Attach the stream AFTER the <video> exists in the DOM (it only renders in
  // the "camera" stage). Attaching inside startCamera raced the render and
  // left a dead/black preview — especially on iOS Safari.
  useEffect(() => {
    if (stage !== "camera" || !streamRef.current || !videoRef.current) return;
    const v = videoRef.current;
    v.srcObject = streamRef.current;
    v.play().catch(() => { /* iOS may defer; playsInline+muted allows autoplay */ });
  }, [stage]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    setErr("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("This browser doesn't support camera access. Try Safari 14+ or Chrome.");
      setStage("error");
      return;
    }
    setStage("starting");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = s;
      setStage("camera");
    } catch (e) {
      const denied = e?.name === "NotAllowedError" || e?.name === "SecurityError";
      setErr(denied
        ? "Camera permission was denied. Enable it in your browser settings (iPhone: Settings → Safari → Camera) and try again."
        : e?.message || "Camera unavailable");
      setStage("error");
    }
  };

  const capture = useCallback(async () => {
    if (!videoRef.current) return;
    setStage("working");
    setCaptureErr("");
    const v = videoRef.current;
    // iOS can report 0x0 for a moment after play() — wait for real frames.
    const ready = await waitForVideoReady(v);
    if (!ready) {
      setCaptureErr("Camera is still warming up — give it a second and try again.");
      setStage("camera");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    let face;
    try {
      face = await extractSingleFace(canvas);
    } catch (e) {
      setCaptureErr("Couldn't load the face engine — check your connection and try again.");
      setStage("camera");
      return;
    }
    if (!face) {
      setCaptureErr("No face detected — get closer, face the camera, and make sure there's light on your face.");
      setStage("camera");
      return;
    }
    // Small face crop → shown as this person's icon in the gallery People strip
    let avatar = null;
    try { avatar = cropFaceAvatar(canvas, face.box); } catch (_) { /* optional */ }
    stopCamera();
    try {
      await Faces.register(slug, { guest_id: guestId, name: effectiveName, descriptor: face.descriptor, avatar });
      try { localStorage.setItem("meyecam.display_name", effectiveName); } catch (_) { /* private mode */ }
      setStage("done");
      onRegistered?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your face");
      setStage("error");
      setErr(e?.response?.data?.detail || "Try again");
    }
  }, [slug, guestId, effectiveName, onRegistered]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 90, background: "rgba(6,4,14,0.9)", backdropFilter: "blur(14px)" }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md p-6 rounded-3xl relative" style={{
        background: "#15131F",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9)",
      }}>
        <button
          type="button"
          onClick={() => { stopCamera(); onClose(); }}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)" }}
          data-testid="face-register-close"
          aria-label="Close"
        >
          <XIcon size={16} />
        </button>

        {stage === "intro" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
                   style={{ background: "rgba(127,75,255,0.16)", border: "1px solid rgba(127,75,255,0.5)" }}>
                <ScanFace size={20} color="#C9B6FF" />
              </div>
              <div>
                <div className="text-lg font-semibold">Add your face to this album</div>
                <div className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                  Other guests can tap your name to see every photo you&rsquo;re in
                </div>
              </div>
            </div>
            <div className="rounded-2xl px-4 py-3 mb-4 text-sm flex items-start gap-3"
                 style={{ background: "rgba(34,255,183,0.08)", border: "1px solid rgba(34,255,183,0.3)" }}>
              <ShieldCheck size={16} color="#22FFB7" className="mt-0.5 shrink-0" />
              <div>
                <div className="font-medium" style={{ color: "#B7FFDA" }}>You&rsquo;re in control</div>
                <div className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>
                  We save a numeric face fingerprint plus a tiny face icon so guests
                  can spot you in the People strip. Only for this album.
                </div>
              </div>
            </div>
            {!(name || "").trim() && (
              <input
                type="text"
                className="meye-input mb-3"
                placeholder="Your name (shown on your face pill)"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                maxLength={60}
                autoComplete="off"
                data-testid="face-register-name-input"
              />
            )}
            <button
              type="button"
              onClick={startCamera}
              disabled={!effectiveName}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-base px-4 py-3"
              style={{
                background: "linear-gradient(90deg, #7F4BFF 0%, #22E2FF 100%)",
                color: "#fff",
                boxShadow: "0 14px 36px -12px rgba(127,75,255,0.55)",
                opacity: effectiveName ? 1 : 0.5,
              }}
              data-testid="face-register-start"
            >
              <Camera size={16} /> Enable camera
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full mt-2 text-sm px-4 py-2 rounded-full"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.75)" }}
              data-testid="face-register-skip"
            >
              Skip for now
            </button>
          </>
        )}

        {stage === "starting" && (
          <div className="py-8 text-center" data-testid="face-register-starting">
            <div className="meye-spinner mx-auto mb-4"/>
            <div className="text-lg font-semibold">Starting camera…</div>
            <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>
              If your browser asks, tap &ldquo;Allow&rdquo;
            </div>
          </div>
        )}

        {stage === "camera" && (
          <>
            <div className="text-sm font-semibold mb-3">Center your face, then tap</div>
            {captureErr && (
              <div
                role="alert"
                className="text-[13px] mb-3 rounded-xl px-3 py-2"
                style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.4)", color: "#FF9B9B" }}
                data-testid="face-register-capture-error"
              >
                {captureErr}
              </div>
            )}
            <div className="relative rounded-2xl overflow-hidden mb-4"
                 style={{ background: "#000", aspectRatio: "4 / 3" }}>
              <video ref={videoRef}
                     className="absolute inset-0 w-full h-full object-cover"
                     style={{ transform: "scaleX(-1)" }}
                     playsInline muted
                     data-testid="face-register-video" />
              <div className="absolute pointer-events-none"
                   style={{ top: "12%", left: "20%", right: "20%", bottom: "12%",
                            border: "2px dashed rgba(255,255,255,0.5)", borderRadius: "50%" }} />
            </div>
            <button type="button" onClick={capture}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-base px-4 py-3"
                    style={{ background: "linear-gradient(90deg, #7F4BFF 0%, #22E2FF 100%)", color: "#fff" }}
                    data-testid="face-register-capture">
              <Camera size={16} /> Take selfie
            </button>
          </>
        )}

        {stage === "working" && (
          <div className="py-8 text-center">
            <div className="meye-spinner mx-auto mb-4"/>
            <div className="text-lg font-semibold">Saving your face fingerprint…</div>
          </div>
        )}

        {stage === "done" && (
          <div className="py-6 text-center">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                 style={{ background: "rgba(34,255,183,0.14)", border: "1px solid rgba(34,255,183,0.5)" }}>
              <Check size={26} color="#22FFB7" />
            </div>
            <div className="text-lg font-semibold mb-1">You&rsquo;re registered!</div>
            <div className="text-sm mb-5" style={{ color: "rgba(255,255,255,0.65)" }}>
              Anyone visiting the gallery can tap <span className="font-semibold text-white">{effectiveName}</span> to
              see every photo of you.
            </div>
            <button type="button" onClick={onClose}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-4 py-3"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}
                    data-testid="face-register-done-close">
              Continue
            </button>
          </div>
        )}

        {stage === "error" && (
          <div className="py-4 text-center">
            <div className="text-lg font-semibold mb-1">Camera not available</div>
            <div className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.6)" }}>
              {err || "Please grant camera permission and try again."}
            </div>
            <button type="button" onClick={() => setStage("intro")}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-4 py-3"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)" }}
                    data-testid="face-register-retry">
              <RefreshCw size={14}/> Try again
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
